import React from "react";
import { Table, Button, Space, Popconfirm, Switch, Tooltip, Tag } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { IRulesSushiLunch } from "../../../redux/@types";

const DAY_LABELS: Record<string, string> = {
  monday: "Lun",
  tuesday: "Mar",
  wednesday: "Mié",
  thursday: "Jue",
  friday: "Vie",
  saturday: "Sáb",
  sunday: "Dom",
};

interface TableRulesFiftyOffProps {
  rules: IRulesSushiLunch[];
  loading: boolean;
  categoryNames: Record<number, string>;
  onEdit: (rule: IRulesSushiLunch) => void;
  onDelete: (id: number) => void;
  onToggleStatus: (id: number, status: "active" | "inactive") => void;
}

function getDaysActive(rule: IRulesSushiLunch) {
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  return days
    .filter((day) => rule[day as keyof IRulesSushiLunch] === "true")
    .map((day) => DAY_LABELS[day])
    .join(", ");
}

function getCategoryIds(rule: IRulesSushiLunch): number[] {
  if (rule.category_ids?.length) {
    return rule.category_ids;
  }
  if (rule.apply_to_category_id) {
    return [rule.apply_to_category_id];
  }
  return [];
}

export const TableRulesFiftyOff: React.FC<TableRulesFiftyOffProps> = ({
  rules,
  loading,
  categoryNames,
  onEdit,
  onDelete,
  onToggleStatus,
}) => {
  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      render: (name: string) => <strong>{name}</strong>,
    },
    {
      title: "Categorías",
      key: "categories",
      render: (_: unknown, record: IRulesSushiLunch) => {
        const ids = getCategoryIds(record);
        if (!ids.length) {
          return <span style={{ color: "#999" }}>Sin categorías</span>;
        }

        return (
          <Space size={[4, 4]} wrap>
            {ids.map((id) => (
              <Tag key={id} color="orange">
                {categoryNames[id] ?? `Categoría ${id}`}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Días",
      key: "days",
      render: (_: unknown, record: IRulesSushiLunch) => (
        <span style={{ fontSize: 12 }}>{getDaysActive(record) || "Ninguno"}</span>
      ),
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: IRulesSushiLunch) => (
        <Switch
          checked={status === "active"}
          onChange={(checked) =>
            onToggleStatus(record.id, checked ? "active" : "inactive")
          }
          checkedChildren="Activo"
          unCheckedChildren="Inactivo"
        />
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_: unknown, record: IRulesSushiLunch) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar esta regla?"
            onConfirm={() => onDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Tooltip title="Eliminar">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      dataSource={rules}
      columns={columns}
      loading={loading}
      rowKey="id"
      pagination={{ pageSize: 10 }}
    />
  );
};
